import { EAuthenticationType } from "../dtos/datasource.dto";
import { EIOSPublishType } from "./app-store-connect-api-settings";
import { EGooglePlayPublishType } from "./google-play-connect-api-settings";
import { EPushNoticationPublishType } from "./push-notification-settings";
import { ERestMethodType } from "./rest-method-type";

interface ICommonGlobalSetting {
  isGlobalCommonConfigOnProjectConfig?: boolean;
}

export enum ELocationAccuray {
  Approximate = 0,
  Precise = 1
}

export enum ELDAPBindType {
  Simple = 0,
  Anonymous = 1,
  Regular = 2
}

export enum ELDAPProtocol {
  LDAP = 0,
  LDAPS = 1
}

export enum ESearchServiceModel {
  OpenAI = 0,
  Onprem = 1
}

export enum EOnPremModelType {
  Qwen25 = 0,
  Mistral = 1,
  Deepseek = 2,
  Llama = 3
}

export enum EBucketUploadType {
  AWS = 0,
  Google = 1,
  Azure = 2,
  MinIO = 3
}

export enum EBucketUploadAWSRegions {
  US_EAST_1 = "us-east-1", // US East (N. Virginia)
  US_EAST_2 = "us-east-2", // US East (Ohio)
  US_WEST_1 = "us-west-1", // US West (N. California)
  US_WEST_2 = "us-west-2", // US West (Oregon)
  AF_SOUTH_1 = "af-south-1", // Africa (Cape Town)
  AP_EAST_1 = "ap-east-1", // Asia Pacific (Hong Kong)
  AP_SOUTH_1 = "ap-south-1", // Asia Pacific (Mumbai)
  AP_SOUTH_2 = "ap-south-2", // Asia Pacific (Hyderabad)
  AP_SOUTHEAST_1 = "ap-southeast-1", // Asia Pacific (Singapore)
  AP_SOUTHEAST_2 = "ap-southeast-2", // Asia Pacific (Sydney)
  AP_SOUTHEAST_3 = "ap-southeast-3", // Asia Pacific (Jakarta)
  AP_NORTHEAST_1 = "ap-northeast-1", // Asia Pacific (Tokyo)
  AP_NORTHEAST_2 = "ap-northeast-2", // Asia Pacific (Seoul)
  AP_NORTHEAST_3 = "ap-northeast-3", // Asia Pacific (Osaka)
  CA_CENTRAL_1 = "ca-central-1", // Canada (Central)
  EU_CENTRAL_1 = "eu-central-1", // Europe (Frankfurt)
  EU_CENTRAL_2 = "eu-central-2", // Europe (Zurich)
  EU_NORTH_1 = "eu-north-1", // Europe (Stockholm)
  EU_SOUTH_1 = "eu-south-1", // Europe (Milan)
  EU_SOUTH_2 = "eu-south-2", // Europe (Spain)
  EU_WEST_1 = "eu-west-1", // Europe (Ireland)
  EU_WEST_2 = "eu-west-2", // Europe (London)
  EU_WEST_3 = "eu-west-3", // Europe (Paris)
  IL_CENTRAL_1 = "il-central-1", // Israel (Tel Aviv)
  ME_CENTRAL_1 = "me-central-1", // Middle East (UAE)
  ME_SOUTH_1 = "me-south-1", // Middle East (Bahrain)
  SA_EAST_1 = "sa-east-1" // South America (São Paulo)
}

export interface IPasswordPolicy extends ICommonGlobalSetting {
  id: string;
  name: string;
  mustNotContainsWords: string[];
  recurringCharsCount: number | null;
  sequentialDigitsCount: number | null;
  notContainsNameSurname: boolean;
  notContainsBirthdate: boolean;
  specialChars: string;
  differentLastPasswordsCount: number | null;
  minimumSpecialChar: number | null;
  minUpperCase: number | null;
  minLowerCase: number | null;
  minDigits: number | null;
  minPasswordLength: number;
  maxPasswordLength: number;
  minPasswordAge: number | null;
  maxPasswordAge: number | null;
  notifyBeforeExpired: number | null;
  tresholdLock: number | null;
  tresholdDuration: number | null;
  incorrectTimes: number | null;
  incorrectEntriesForEnable: number | null;
  configIds?: [];
}

export interface ISMTPSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  serverAddress: string;
  serverPort: number;
  userName: string;
  emailAddress: string;
  emailPassword: string;
  enableSSL: boolean;
  configIds?: [];
}

export interface IGoogleMapsSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export interface IAMapSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  /** Amap platform key (JSAPI on web, Android SDK key, iOS SDK key). */
  apiKey: string;
  /** Amap JSAPI 2.0 web security code (`window._AMapSecurityConfig.securityJsCode`). */
  securityJsCode: string;
  /** Web Service (REST) key for geocode/route calls. Falls back to `apiKey` when empty. */
  webServiceKey?: string;
  /**
   * Enables Amap's World Map (世界地图) service — overseas tiles + base-map labels in the app's
   * language. Requires the World Map entitlement on the key. Absent → false (mainland-only).
   */
  enableWorldMap?: boolean;
  configIds?: [];
}

export interface IAWSSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  accessKeyId: string | null;
  secretAccessKey: string | null;
  defaultFromEMail: string | null;
  selectedRegion: number | null;
  configIds?: [];
}

export interface IStripeSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  publishKey: string;
  secretKey: string;
  webhookSecret: string;
  title: string;
  configIds?: [];
}

export interface IIyzicoSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  secretKey: string;
  baseUrl: string;
  title: string | null;
  configIds?: [];
}

export interface IGoogleAuthSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  clientId: string;
  clientSecret: string;
  serverClientId: string;
  serverClientSecret: string;
  iosUrlScheme: string;
  configIds?: [];
}

export interface IFacebookAuthSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  appId: string;
  clientToken: string;
  configIds?: [];
}

export interface IAppStoreConnectApiSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  p8FileName?: string;
  p8FileContent?: string;
  p8FileKey?: string;
  issuerId: string;
  appleId: string;
  appStoreConnectTeamId: string;
  developerTeamId: string;
  publishType?: EIOSPublishType;
  appetizeApiToken: string;
  appstorePublishType?: EPushNoticationPublishType;
  inAppPurchaseP8FileName?: string;
  inAppPurchaseP8FileContent?: string;
  inAppPurchaseP8FileKey?: string;
  configIds?: [];
}

export interface IGooglePlayApiSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  publishType: EGooglePlayPublishType;
  appetizeApiToken?: string;
  jksFileName?: string;
  signingStorePassword?: string;
  signingKeyAlias?: string;
  signingKeyPassword?: string;
  sha256CertificateFingerprint?: string;
  subscriptionServiceAccountJson?: string;
  configIds?: [];
}

export interface IFirebaseSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  senderId: string;
  senderKey: string;
  notificationCertificate: string;
  serviceAccountJson: string;
  configIds?: [];
}

export interface IFirebaseIOSSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  googleServiceInfoPlistFileName: string;
  configIds?: [];
}

export interface IPushNotificationSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  publishType: number;
  teamId: string;
  notificationCertificate: string;
  configIds?: [];
}

export interface IPushNotificationSettingWeb extends ICommonGlobalSetting {
  id: string;
  name: string;
  privateKey: string;
  publicKey: string;
  configIds?: [];
}

export interface ICameraPermissionLabelSetting {
  id: string;
  name: string;
  values?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
  configIds?: [];
}

export interface IBluetoothPermissionLabelSetting {
  id: string;
  name: string;
  values?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
  configIds?: [];
}

export interface ILocationPermissionLabelSetting {
  id: string;
  name: string;
  values?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
  configIds?: [];
}

export interface INotificationPermissionLabelSetting {
  id: string;
  name: string;
  values?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
  configIds?: [];
}

export interface IInversCloudBoxxSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiBaseUrl: string;
  apiKey: string;
  configIds?: [];
}

export interface ICommonMobileSetting {
  id: string;
  name: string;
  associatedDomain: string;
  AssociatedSubDomain?: string;
  configIds?: [];
}

export interface IOTPSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  period: number;
  digit: number;
  secretKey: string;
  algorithm: "SHA1" | "SHA256" | "SHA512";
  configIds?: [];
}

export interface IGenericAuthSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  methodType?: ERestMethodType;
  url: string;
  tokenExpirationInMinutes: number;
  contentType: string;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  bodyParameters: { [key: string]: string };
  configIds?: string[];
}
export interface IOAuth2Setting extends ICommonGlobalSetting {
  id: string;
  name: string;
  accessTokenUrl: string;
  accessTokenExpirationInMinutes: number;
  clientId: string;
  clientSecret: string;
  scope: string;
  clientAuthSendAsBasicAuthHeader: boolean;
  configIds?: string[];
}
export interface ILDAPSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  ipAddress: string;
  userName: string;
  password: string;
  port: number;
  dn: string;
  searchDn: string;
  searchTemplate: string;
  bindType: ELDAPBindType;
  protocol: ELDAPProtocol;
  userNameAttributeName: string;
  firstNameAttributeName: string;
  lastNameAttributeName: string;
  phoneNumberAttributeName: string;
}

export interface IGpsTrackingSetting {
  id: string;
  name: string;
  distanceBetweenLocationUpdates: number;
  locationAccuracy: ELocationAccuray;
  backgroundLocationTrackingEnabled: boolean;
}

export interface IGpsTrackingPermissionLabelSetting {
  id: string;
  name: string;
  values?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
  configIds?: [];
}

export interface IGpsTrackingBackgroundPermissionLabelSetting {
  id: string;
  name: string;
  values?: {
    languageName: string;
    cultureName: string;
    value: string;
  }[];
  configIds?: [];
}

export interface IAdiRegistrationSetting {
  id: string;
  name: string;
  snippet: string;
  configIds?: string[];
}

export interface IAppleSignInSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  teamId: string;
  p8FileName: string;
}

export interface IMSALSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  clientId: string;
  clientSecret: string;
  authority?: string;
  scopes?: string;
  keystoreBase64: string;
  cacheLocation: string;
  cacheLocationEnabled: boolean;
  configIds?: string[];
}
export interface IDBConnectionStringSetting {
  id: string;
  name: string;
  dbConnectionString: string;
}

export interface IBackendUrlSetting {
  id: string;
  name: string;
  backendUrl: string;
}

export interface IFrontendUrlSetting {
  id: string;
  name: string;
  virtualDirectory: string;
}

export interface ISessionSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  timeoutMins: string | null;
  logoutCountdownSeconds: string | null;
  persistSessionAcrossPageReloads: boolean;
}

export interface IWorkflowEngineBaseUrlSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  baseUrl: string;
}

export interface IParamPaymentSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  clientCode: string;
  clientUserName: string;
  clientPassword: string;
  clientGuid: string;
  baseUrl: string;
  configIds?: string[];
}

export interface ISearchServiceSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  baseUrl?: string;
  qdrantServicePort?: number;
  pythonServicePort?: number;
  qdrantStoragePath?: string;
  modelType: ESearchServiceModel;
  onPremModelType: EOnPremModelType;
  configIds?: string[];
}

export interface INotifyActionsDefaultDurationSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  successDuration?: number;
  infoDuration?: number;
  warningDuration?: number;
  errorDuration?: number;
  defaultDuration?: number;
}

export interface IOpenAISetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export interface IAzureOpenAISetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  endpoint: string;
  deploymentName: string;
  apiVersion: string;
  configIds?: [];
}

export interface IGeminiSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  serviceAccountJson: string;
  /**
   * Vertex AI location for this credential's Gemini requests. Absent or empty means the global
   * endpoint, which pools capacity across regions and so returns far fewer 429 RESOURCE_EXHAUSTED
   * errors than any single region. Set a region (e.g. `europe-west4`) only when the data must be
   * processed there, since Google chooses the processing region on the global endpoint.
   *
   * Any Vertex AI location id is accepted; `VERTEX_AI_LOCATION_SUGGESTIONS` only populates the
   * designer's picker.
   */
  location?: string;
  configIds?: [];
}

/**
 * Vertex AI locations offered in the Gemini setting's Location picker, `global` first and then by
 * geography.
 *
 * SUGGESTIONS ONLY — nothing validates against this array. The field stays free text, so a location
 * Google adds later works the day it ships without a release here, and an entry that stops being
 * served fails at project generation, which probes the real endpoint with a `generateContent` call
 * and surfaces Google's own message. Treat a stale entry as harmless and never turn this into a
 * permitted-value set.
 */
export const VERTEX_AI_LOCATION_SUGGESTIONS: readonly string[] = [
  "global",
  // United States
  "us-central1",
  "us-east1",
  "us-east4",
  "us-east5",
  "us-south1",
  "us-west1",
  "us-west4",
  // Canada and South America
  "northamerica-northeast1",
  "southamerica-east1",
  // Europe
  "europe-central2",
  "europe-north1",
  "europe-southwest1",
  "europe-west1",
  "europe-west2",
  "europe-west3",
  "europe-west4",
  "europe-west6",
  "europe-west8",
  "europe-west9",
  // Asia Pacific
  "asia-east1",
  "asia-east2",
  "asia-northeast1",
  "asia-northeast3",
  "asia-south1",
  "asia-southeast1",
  "australia-southeast1",
  // Middle East
  "me-central1",
  "me-central2",
  "me-west1"
];

export interface ISerperToolSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export interface IFileUploadPathSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  fileUploadPath: string;
}

export interface ISapServiceBaseUrlSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  sapServiceBaseUrl: string;
}

export interface IWhatsappBusinessSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  accessToken: string;
  phoneNumberId: string;
  versionNumber: string;
  appId: string;
  appSecret: string;
  businessAccountId: string;
}

export interface ICommonSettings {
  passwordPolicies: IPasswordPolicy[] | null;
  smtpSettings: ISMTPSetting[] | null;
  googleMapsSettings: IGoogleMapsSetting[] | null;
  amapSettings?: IAMapSetting[] | null;
  awsSettings: IAWSSetting[] | null;
  stripeSettings: IStripeSetting[] | null;
  iyzicoSettings: IIyzicoSetting[] | null;
  paramPaymentSettings: IParamPaymentSettings[] | null;
  genericAuthSettings: IGenericAuthSetting[] | null;
  oAuth2Settings: IOAuth2Setting[] | null;
  ldapSettings: ILDAPSetting[] | null;
  googleAuth: IGoogleAuthSetting[] | null;
  facebookAuth: IFacebookAuthSetting[] | null;
  appStoreConnectApiSettings: IAppStoreConnectApiSetting[] | null;
  googlePlayApiSettings: IGooglePlayApiSetting[] | null;
  firebaseSettings: IFirebaseSetting[] | null;
  iosFirebaseSettings: IFirebaseIOSSetting[] | null;
  pushNotificationSettings: IPushNotificationSetting[] | null;
  pushNotificationSettingsWeb: IPushNotificationSettingWeb[] | null;
  cameraPermissionLabelSettings: ICameraPermissionLabelSetting[] | null;
  locationPermissionLabelSettings: ILocationPermissionLabelSetting[] | null;
  notificationPermissionLabelSettings: INotificationPermissionLabelSetting[] | null;
  bluetoothPermissionLabelSettings: IBluetoothPermissionLabelSetting[] | null;
  commonMobileSettings: ICommonMobileSetting[] | null;
  inversCloudBoxxSettings: IInversCloudBoxxSetting[] | null;
  gpsTrackingSettings: IGpsTrackingSetting[] | null;
  gpsTrackingPermissionLabelSettings: IGpsTrackingPermissionLabelSetting[] | null;
  gpsTrackingBackgroundPermissionLabelSettings: IGpsTrackingBackgroundPermissionLabelSetting[] | null;
  appleSignInSettings: IAppleSignInSetting[] | null;
  msalSettings: IMSALSetting[] | null;
  dbConnectionStringSettings: IDBConnectionStringSetting[] | null;
  restApiConnectionSettings: IRestApiConnectionSetting[] | null;
  backendUrlSettings: IBackendUrlSetting[] | null;
  frontendUrlSettings: IFrontendUrlSetting[] | null;
  sessionSettings: ISessionSetting[] | null;
  workflowEngineBaseUrlSettings: IWorkflowEngineBaseUrlSettings[] | null;
  otpSettings: IOTPSetting[] | null;
  notifyActionsDurationsSettings: INotifyActionsDefaultDurationSettings[] | null;
  searchServiceSettings: ISearchServiceSettings[] | null;
  openAISettings: IOpenAISetting[] | null;
  azureOpenAISettings: IAzureOpenAISetting[] | null;
  geminiSettings: IGeminiSetting[] | null;
  serperToolSettings: ISerperToolSetting[] | null;
  whatsappBusinessSettings: IWhatsappBusinessSettings[] | null;
  fileUploadPathSettings: IFileUploadPathSetting[] | null;
  sapServiceBaseUrlSettings: ISapServiceBaseUrlSetting[] | null;
  googleRecaptchaSettings: IGoogleRecaptchaSettings[] | null;
  googleRecaptchaIosSettings: IGoogleRecaptchaMobileSettings[] | null;
  googleRecaptchaAndroidSettings: IGoogleRecaptchaMobileSettings[] | null;
  exchangeServerMailSettings: IExchangeServerMailSettings[] | null;
  cacheSettings: ICacheSetting[] | null;
  twinSettings: ITwinSetting[] | null;
  bucketUploadSettings: IBucketUploadSetting[] | null;
  braveSearchSettings: IBraveSearchSetting[] | null;
  slackSettings: ISlackSetting[] | null;
  dropboxSettings: IDropboxSetting[] | null;
  githubSettings: IGithubSetting[] | null;
  googleMcpSettings: IGoogleMcpSetting[] | null;
  googleMapMcpSettings: IGoogleMcpMapSetting[] | null;
  amapWebServiceSettings?: IAMapWebServiceSetting[] | null;
  jiraMcpSettings: IJiraMcpSetting[] | null;
  youtubeMcpSettings: IYoutubeMcpSetting[] | null;
  trelloMcpSettings: ITrelloMcpSetting[] | null;
  office365McpSettings: IOffice365McpSetting[] | null;
  firecrawlMcpSettings: IFirecrawlMcpSetting[] | null;
  elasticApmSettings: IElasticApmSetting[] | null;
  apifySettings: IApifySetting[] | null;
  ipRateLimitingSettings: IIpRateLimitingSetting[] | null;
  documentManagementSettings: IDocumentManagementSettings[] | null;
  searchEngineSettings: ISearchEngineSettings[] | null;
  adiRegistrationSettings: IAdiRegistrationSetting[] | null;
}

export interface IGoogleRecaptchaSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  siteKey: string;
  secretKey: string;
}

export interface IGoogleRecaptchaMobileSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  siteKey: string;
  configIds?: [];
}

export interface IExchangeServerMailSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  serverAddress: string;
  emailAddress: string;
  emailPassword: string;
  autoDiscoverServerAddress: boolean;
}
export enum ECacheType {
  Redis = 2
}

export interface ICacheSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  type: ECacheType;
  redisConnectionString: string;
  configIds?: string[];
}

// Twin live-dashboard embed setting. `url` is the Twin deployment URL (non-secret);
// `embedToken` is the tenant access secret (handled like other common-setting
// secrets — encrypted at rest, served to the embedded board at runtime).
export interface ITwinSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  url: string;
  embedToken: string;
  // When true, the Twin chat FAB is shown app-wide (non-auth screens). When false,
  // chat can still be added per-screen via the Twin Chat design element.
  chatEnabled?: boolean;
  // Required identity secret (kS_…) — the HMAC key for the per-end-user identify JWT.
  // Server-side only (encrypted at rest, never sent to the browser); the backend signs
  // a short-lived JWT with it so every embedded viewer is a real KuikAgents user.
  identitySecret: string;
  configIds?: string[];
}

export interface IBucketUploadSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  type: EBucketUploadType;
  awsBucketName?: string;
  awsRegion?: EBucketUploadAWSRegions;
  awsAccessKeyId?: string;
  awsSecretAccessKey?: string;
  googleBucketName?: string;
  googleServiceAccountKeyJson?: string;
  azureConnectionString?: string;
  azureContainerName?: string;
  minioEndpoint?: string;
  minioAccessKey?: string;
  minioSecretKey?: string;
  minioBucketName?: string;
  encryptionKey?: string;
  encryptionIV?: string;
  configIds?: [];
}

export interface IBraveSearchSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export interface ISlackSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  botToken: string;
  teamId: string;
  configIds?: [];
}

export interface IDropboxSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  appKey: string;
  appSecret: string;
  configIds?: [];
}

export interface IGithubSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  accessToken: string;
  configIds?: [];
}

export interface IGoogleMcpSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  clientId: string;
  clientSecret: string;
  searchApiKey: string;
  searchEngineId: string;
  emailAddress: string;
  configIds?: [];
}

export interface IGoogleMcpMapSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export interface IAMapWebServiceSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  /** Amap Web Service (REST) key used by server-side geocoding/place/route calls. */
  apiKey: string;
  configIds?: [];
}

export interface IJiraMcpSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  email: string;
  apiToken: string;
  host: string;
  configIds?: [];
}

export interface IYoutubeMcpSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  language: string;
  configIds?: [];
}

export interface ITrelloMcpSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  token: string;
  configIds?: [];
}

export interface IOffice365McpSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  clientId: string;
  clientSecret: string;
  tenantId: string;
  scope: string;
  configIds?: [];
}

export interface IFirecrawlMcpSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export interface IApifySetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  apiKey: string;
  configIds?: [];
}

export enum EElasticApmLogLevel {
  Trace = "trace",
  Debug = "debug",
  Info = "info",
  Warning = "warning",
  Error = "error",
  Critical = "critical",
  Off = "off"
}

export enum EElasticApmCaptureBody {
  Off = "off",
  Errors = "errors",
  Transactions = "transactions",
  All = "all"
}

export interface IElasticApmSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  // Connection Settings
  serverUrl: string;
  serviceName: string;
  secretToken?: string;
  environment?: string;
  // Transaction/Span Settings
  transactionSampleRate?: number;
  spanFramesMinDuration?: number;
  captureBody?: EElasticApmCaptureBody;
  transactionMaxSpans?: number;
  // Error/Logging Settings
  logLevel?: EElasticApmLogLevel;
  stackTraceLimit?: number;
  // Performance Settings
  metricsInterval?: number;
  captureHeaders?: boolean;
  // Frontend Settings
  enableFrontendTracing?: boolean;
  configIds?: [];
}

export interface IRestApiConnectionSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  baseUrl: string;
  authType: EAuthenticationType;
  clientIdKeyName?: string;
  clientId?: string;
  clientSecretKeyName?: string;
  clientSecret?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
}

export enum EIpRateLimitIdentity {
  Ip = 0,
  ClientId = 1
}

export interface IIpRateLimitRule {
  period: string;
  limit: number;
}

export interface IIpRateLimitingSetting extends ICommonGlobalSetting {
  id: string;
  name: string;
  enabled: boolean;
  identity: EIpRateLimitIdentity;
  generalRules: IIpRateLimitRule[];
  configIds?: [];
}

export interface IOnlyOfficeSettings {
  enabled: boolean;
  documentServerUrl: string;
  jwtSecret: string;
}

export interface ICollaboraSettings {
  enabled: boolean;
  serverUrl: string;
  wopiTokenSecret: string;
  wopiDiscoveryUrl: string;
}

export interface IDrawioSettings {
  /** Self-hosted draw.io server URL. Empty = public embed.diagrams.net (internet required). */
  serverUrl: string;
}

export interface IDocumentManagementSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  masterKey: string;
  maxFileSizeBytes: number;
  requireAuditKey: boolean;
  auditLogRetentionDays: number;
  maxWatermarkFileSizeBytes?: number;
  chunkSizeBytes?: number;
  maxChunksPerUpload?: number;
  checkOutExpiryHours?: number;
  wopiTokenExpiryMinutes?: number;
  lockCleanupIntervalMinutes?: number;
  shareRateLimiterMaxRequests?: number;
  shareRateLimiterWindowSeconds?: number;
  maxAccessibleDocuments?: number;
  onlyOffice: IOnlyOfficeSettings;
  collabora: ICollaboraSettings;
  drawio: IDrawioSettings;
  configIds?: string[];
}

export interface ISearchEngineSettings extends ICommonGlobalSetting {
  id: string;
  name: string;
  enabled: boolean;
  provider: string;
  url: string;
  username: string;
  password: string;
  defaultIndexPrefix: string;
  tlsVerify: boolean;
  connectionTimeoutSeconds: number;
  maxResultsPerQuery: number;
  highlightFragmentSize: number;
  configIds?: string[];
}
